import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { DashboardPage } from "../features/dashboard/pages/DashboardPage.jsx";
import { ItemsPage } from "../features/items/pages/ItemsPage.jsx";
import { InventoryPage } from "../features/inventory/pages/InventoryPage.jsx";
import { InvoicesPage } from "../features/invoices/pages/InvoicesPage.jsx";
import { PurchasesPage } from "../features/purchases/pages/PurchasesPage.jsx";
import { CustomersPage } from "../features/customers/pages/CustomersPage.jsx";
import { SettingsPage } from "../features/settings/pages/SettingsPage.jsx";
import { BankingPage } from "../features/banking/pages/BankingPage.jsx";
import { Header } from "../shared/components/Header.jsx";
import { BottomNav } from "../shared/components/BottomNav.jsx";
import { SideNav } from "../shared/components/SideNav.jsx";
import { ShortcutHelp } from "../shared/shortcuts/ShortcutHelp.jsx";
import { useShortcut } from "../shared/shortcuts/useShortcut.js";
import { useConfig } from "../shared/context/ConfigContext.jsx";
import { useTheme } from "../shared/context/ThemeContext.jsx";
import { PageHeadingProvider } from "../shared/context/PageHeadingContext.jsx";
import { ROUTES, isRouteEnabled, pathForTab, routeForPath } from "./routeConfig.js";

/** Renders the page only when its feature is enabled in config; otherwise back to Home. */
function FeatureRoute({ featureKey, children }) {
  const config = useConfig();
  const features = config?.features || {};
  if (featureKey && features[featureKey] === false) return <Navigate to="/" replace />;
  return children;
}

/** Scroll to top when moving between pages (not when opening/closing a bill on the same page). */
function ScrollToTop() {
  const { pathname } = useLocation();
  const section = pathname.split("/").filter(Boolean)[0] || "";
  const lastSection = useRef(section);

  useEffect(() => {
    if (lastSection.current !== section) {
      lastSection.current = section;
      window.scrollTo(0, 0);
    }
  }, [section]);

  return null;
}

const SIDEBAR_COLLAPSED_KEY = "books_sidebar_collapsed";

/** Desktop-only global shortcuts: sidebar, theme, help, and "G then <letter>" navigation. */
function GlobalShortcuts({ onToggleSidebar, onShowHelp }) {
  const config = useConfig();
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  const features = config?.features || {};
  const goTo = (routeId) => () => {
    const route = ROUTES.find((r) => r.id === routeId);
    if (route && isRouteEnabled(route, features)) navigate(route.path);
  };

  useShortcut("toggle_sidebar", onToggleSidebar);
  useShortcut("toggle_theme", toggleTheme, { allowInOverlay: true });
  useShortcut("show_help", onShowHelp);
  useShortcut("go_home", goTo("dashboard"));
  useShortcut("go_invoices", goTo("invoices"));
  useShortcut("go_purchases", goTo("purchases"));
  useShortcut("go_items", goTo("items"));
  useShortcut("go_customers", goTo("customers"));
  useShortcut("go_inventory", goTo("inventory"));
  useShortcut("go_banking", goTo("banking"));
  useShortcut("go_settings", goTo("settings"));
  return null;
}

export function AppShell({ user, onLogout }) {
  const config = useConfig();
  const location = useLocation();
  const navigate = useNavigate();

  const currentRoute = routeForPath(location.pathname);
  const headerTitle = currentRoute?.title || config?.name || "Books Invoicing";
  const dashboard = <DashboardPage user={user} onNavigate={(tab) => navigate(pathForTab(tab))} />;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1");
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const toggleSidebar = () => setSidebarCollapsed((prev) => !prev);
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  return (
    <PageHeadingProvider>
      <div className={`app-container ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        <ScrollToTop />
        <GlobalShortcuts onToggleSidebar={toggleSidebar} onShowHelp={() => setIsHelpOpen(true)} />
        <SideNav collapsed={sidebarCollapsed} onToggle={toggleSidebar} onShowHelp={() => setIsHelpOpen(true)} />
        <Header
          title={headerTitle}
          route={currentRoute}
          user={user}
          onLogout={onLogout}
          onToggleSidebar={toggleSidebar}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={dashboard} />
            <Route path="/items" element={<FeatureRoute featureKey="items"><ItemsPage /></FeatureRoute>} />
            <Route path="/inventory" element={<FeatureRoute featureKey="inventory"><InventoryPage /></FeatureRoute>} />
            <Route path="/invoices/:invoiceId?" element={<FeatureRoute featureKey="invoices"><InvoicesPage /></FeatureRoute>} />
            <Route path="/purchases/:purchaseId?" element={<FeatureRoute featureKey="purchases"><PurchasesPage /></FeatureRoute>} />
            <Route path="/customers" element={<FeatureRoute featureKey="customers"><CustomersPage /></FeatureRoute>} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/banking" element={<BankingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <BottomNav />
        <ShortcutHelp isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
      </div>
    </PageHeadingProvider>
  );
}
